// javascript
import React, { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import Draggable from "gsap/Draggable";
import InertiaPlugin from "gsap/InertiaPlugin";
gsap.registerPlugin(Draggable, InertiaPlugin);
import { l } from '../../../lang/index.jsx';

export default function Career({lang, data}) {
    const viewportRef = useRef(null);
    const railRef = useRef(null);
    const instancesRef = useRef([]);
    const positionsRef = useRef([]);
    const lastActiveRef = useRef(-1);
    const [active, setActive] = useState(0);
    const contentRef = useRef(null);

    // Récupère la liste traduite (autoriser objets/arrays)
    const translated = data ?? l('career', lang, true);
    const DATA = Array.isArray(translated) ? translated : [];

    // Ensure active index is valid when DATA changes
    useEffect(() => {
        if (!DATA || DATA.length === 0) {
            setActive(0);
            return;
        }
        if (active >= DATA.length) {
            setActive(0);
        }
    }, [DATA]);

    useEffect(() => {
        if (!Draggable || !gsap) return;
        const viewport = viewportRef.current;
        const rail = railRef.current;
        if (!viewport || !rail) return;

        positionsRef.current = [];
        let rafId = null;

        const computePositions = () => {
            const markers = Array.from(rail.querySelectorAll(".marker"));
            const railRect = rail.getBoundingClientRect();
            const viewportRect = viewport.getBoundingClientRect();
            const viewportCenterPage = viewportRect.left + viewportRect.width / 2;

            const minX = Math.min(0, viewportRect.width - railRect.width);
            const maxX = 0;

            positionsRef.current = markers.map(m => {
                const mr = m.getBoundingClientRect();
                const markerCenterPage = mr.left + mr.width / 2;
                const targetX = viewportCenterPage - markerCenterPage;
                return Math.max(minX, Math.min(maxX, targetX));
            });

            const threshold = Math.max(24, Math.round(viewportRect.width * 0.04));
            positionsRef.current._min = minX;
            positionsRef.current._max = maxX;
            positionsRef.current._threshold = threshold;
        };

        computePositions();

        const nearestIndex = (x) => {
            const pos = positionsRef.current;
            if (!pos.length) return 0;
            let ni = 0, md = Infinity;
            for (let i = 0; i < pos.length; i++) {
                const d = Math.abs(x - pos[i]);
                if (d < md) { md = d; ni = i; }
            }
            return ni;
        };

        const centeredIndex = (x) => {
            const pos = positionsRef.current;
            if (!pos.length) return -1;
            const threshold = pos._threshold ?? 32;
            let ni = -1, md = Infinity;
            for (let i = 0; i < pos.length; i++) {
                const d = Math.abs(x - pos[i]);
                if (d < md) { md = d; ni = i; }
            }
            return md <= threshold ? ni : -1;
        };

        const updateActive = (idx) => {
            if (idx < 0) return;
            if (lastActiveRef.current === idx) return;
            lastActiveRef.current = idx;

            if (!contentRef.current || !gsap) {
                setActive(idx);
                return;
            }

            gsap.killTweensOf(contentRef.current);
            gsap.to(contentRef.current, {
                opacity: 0,
                y: -8,
                duration: 0.18,
                ease: "power1.in",
                onComplete: () => setActive(idx)
            });
        };

        const scheduleActiveUpdate = (x) => {
            if (rafId) return;
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const idx = centeredIndex(x);
                if (idx >= 0) updateActive(idx);
            });
        };

        rail.style.willChange = rail.style.willChange || "transform";
        rail.style.touchAction = rail.style.touchAction || "pan-y";

        const created = Draggable.create(rail, {
            type: "x",
            bounds: { minX: positionsRef.current._min ?? 0, maxX: positionsRef.current._max ?? 0 },
            inertia: true,
            edgeResistance: 0.9,
            allowContextMenu: true,
            snap: {
                x: (val) => {
                    const pos = positionsRef.current;
                    if (!pos.length) return val;
                    const minX = pos._min ?? val;
                    const maxX = pos._max ?? val;
                    const idx = nearestIndex(val);
                    const snapValue = pos[idx] ?? val;
                    return Math.max(minX, Math.min(maxX, snapValue));
                }
            },
            onPress() { this.target.style.cursor = "grabbing"; },
            onRelease() { this.target.style.cursor = "grab"; },
            onDrag() { scheduleActiveUpdate(this.x); },
            onThrowUpdate() { scheduleActiveUpdate(this.x); },
            onDragEnd() {},
            onThrowComplete() {
                const pos = positionsRef.current;
                const idx = nearestIndex(this.x);
                const minX = pos._min ?? this.x;
                const maxX = pos._max ?? this.x;
                const finalX = Math.max(minX, Math.min(maxX, pos[idx] ?? this.x));
                gsap.set(this.target, { x: finalX, overwrite: true });
                try { this.x = finalX; } catch (e) {}
                updateActive(idx);
            }
        });

        const instance = created && created[0];
        if (instance) instancesRef.current.push(instance);

        const onResize = () => {
            computePositions();
            instancesRef.current.forEach(inst => {
                if (inst && inst.vars) {
                    inst.vars.bounds = { minX: positionsRef.current._min, maxX: positionsRef.current._max };
                }
                if (typeof inst.applyBounds === "function") inst.applyBounds && inst.applyBounds();
                const curIdx = lastActiveRef.current >= 0 ? lastActiveRef.current : 0;
                const tx = positionsRef.current[curIdx] ?? 0;
                gsap.set(rail, { x: tx });
                if (inst) inst.x = tx;
            });
        };

        window.addEventListener("resize", onResize);

        if (positionsRef.current.length) {
            gsap.set(rail, { x: positionsRef.current[0] });
            lastActiveRef.current = 0;
            setActive(0);
        }

        return () => {
            window.removeEventListener("resize", onResize);
            if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
            instancesRef.current.forEach(i => { try { i.kill(); } catch (e) {} });
            instancesRef.current = [];
        };
    }, []);

    // animation d'entrée (après setActive)
    useEffect(() => {
        if (!contentRef.current || !gsap) return;
        gsap.killTweensOf(contentRef.current);
        gsap.fromTo(
            contentRef.current,
            { opacity: 0, y: 8 },
            { opacity: 1, y: 0, duration: 0.35, ease: "power1.out" }
        );
    }, [active]);

    return (
        <div className="career-block">
            <div className="timeline-wrapper">
                <div
                    className="timeline-viewport"
                    ref={viewportRef}
                >
                    <div
                        className="timeline-rail cursor-event"
                        ref={railRef}
                    >
                        <div/>
                        <div>
                            {DATA.map((d, i) => (
                                <div
                                    key={i}
                                    className={`marker${i === active ? " active" : ""}`}
                                ><div/>
                                    <div>{d.date}</div>
                                </div>
                            ))}
                        </div>
                        <div />
                    </div>
                </div>

                <div className="timeline-content" ref={contentRef}>
                    <h3 className="h3" dangerouslySetInnerHTML={{ __html: DATA[active]?.title ?? "" }}></h3>
                    <small>{DATA[active]?.content}</small>
                </div>
            </div>
        </div>
    );
}
